/** TanStack Query hooks for admin settings (generation, AI mode, revision chat). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/shared/api/client';
import type { GenerationSettings, ChatSettings } from '@formency/shared';

// Re-export for convenience
export type { GenerationSettings, ChatSettings } from '@formency/shared';

// ─── Query key factory ──────────────────────────────────────────────────────

export const settingsKeys = {
  generation: ['f', 'settings', 'generation'] as const,
  revisionChat: ['f', 'settings', 'revision-chat'] as const,
  aiMode: ['f', 'settings', 'ai-mode'] as const,
};

// ─── Generation settings ────────────────────────────────────────────────────

export function useGenerationSettings() {
  return useQuery<GenerationSettings>({
    queryKey: settingsKeys.generation,
    queryFn: () => api.get<GenerationSettings>('/settings/generation'),
  });
}

export function useUpdateGenerationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<GenerationSettings>) =>
      api.put<GenerationSettings>('/settings/generation', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: settingsKeys.generation });
      toast.success('Generation settings saved');
    },
    onError: () => {
      toast.error('Failed to save generation settings');
    },
  });
}

// ─── Revision chat settings ─────────────────────────────────────────────────

export function useChatSettings() {
  return useQuery<ChatSettings>({
    queryKey: settingsKeys.revisionChat,
    queryFn: () => api.get<ChatSettings>('/settings/revision-chat'),
  });
}

export function useUpdateChatSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ChatSettings>) =>
      api.put<ChatSettings>('/settings/revision-chat', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: settingsKeys.revisionChat });
      toast.success('Revision chat settings saved');
    },
    onError: () => {
      toast.error('Failed to save revision chat settings');
    },
  });
}

// ─── AI mode ─────────────────────────────────────────────────────────────────

export function useAiMode() {
  return useQuery<{ mode: 'live' | 'testing' }>({
    queryKey: settingsKeys.aiMode,
    queryFn: () => api.get<{ mode: 'live' | 'testing' }>('/settings/ai-mode'),
  });
}

export function useUpdateAiMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { mode: 'live' | 'testing' }) =>
      api.put<{ mode: 'live' | 'testing' }>('/settings/ai-mode', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: settingsKeys.aiMode });
      toast.success('AI mode updated');
    },
  });
}
